import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { AppBanner } from '../components/AppBanner';
import { NamePrompt } from '../components/NamePrompt';
import { useStoryByCode, useJoinStory, useJoinStoryAnonymous } from '../hooks/useStories';
import { useAuthStore } from '../stores/auth.store';
import { useAnonymousStore } from '../stores/anonymous.store';
import { useUILocale } from '../hooks/useUILocale';
import { withLocale } from '../locale';
import { GENRE_LABELS } from '@storychain/shared';

const JoinPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const uiLocale = useUILocale();

  // Auth state
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  // Anonymous state
  const {
    getParticipation,
    setParticipation,
    getOrCreateAnonymousId,
    displayName: savedDisplayName,
    setDisplayName,
  } = useAnonymousStore();

  const existingParticipation = code ? getParticipation(code) : null;

  const hasAutoJoined = useRef(false);
  const [joinError, setJoinError] = useState('');
  const [showNamePrompt, setShowNamePrompt] = useState(false);

  const { data, isLoading, error } = useStoryByCode(code);
  const joinMutation = useJoinStory();
  const joinAnonymousMutation = useJoinStoryAnonymous();

  // Check if already participated. Auth participation wins over cached
  // anonymous: a user who signed in after a previous anon join should land
  // on the auth write flow, not get redirected back into a stale anon session.
  useEffect(() => {
    if (!data?.story || !code) return;

    // userId is populated by the API as { id, name, avatar } — handle both shapes.
    const alreadyJoined =
      isAuthenticated &&
      user &&
      data.story.participants.some((p: { userId?: unknown }) => {
        const uid = p.userId as string | { id?: string; _id?: string } | undefined;
        if (typeof uid === 'string') return uid === user.id;
        return uid?.id === user.id || uid?._id === user.id;
      });

    if (alreadyJoined) {
      navigate(`/story/${data.story.id}/write`, { replace: true });
      return;
    }

    if (!isAuthenticated && existingParticipation) {
      navigate(`/story/${existingParticipation.storyId}/write?anon=1`, { replace: true });
    }
  }, [data, code, existingParticipation, isAuthenticated, user, navigate]);

  // Note: no auto-join on link open. The user should preview the story and
  // explicitly tap "Join as {name}" to consent. Same reason as mobile.

  // Handle authenticated join
  const handleAuthenticatedJoin = async () => {
    if (!code) return;

    try {
      setJoinError('');
      const result = await joinMutation.mutateAsync(code);
      localStorage.removeItem('pendingJoinCode');
      navigate(`/story/${result.story.id}/write`, { replace: true });
    } catch (err: any) {
      setJoinError(err.message || 'Failed to join story');
    }
  };

  // Handle anonymous join (after name prompt)
  const handleAnonymousJoin = async (name: string) => {
    if (!code || !data?.story) return;

    try {
      setJoinError('');
      const anonymousId = getOrCreateAnonymousId();
      const result = await joinAnonymousMutation.mutateAsync({
        code,
        anonymousId,
        displayName: name,
      });

      // Store participation in localStorage
      setParticipation(code, {
        order: result.participation.order,
        displayName: name,
        storyId: result.story.id,
      });
      setDisplayName(name);

      navigate(`/story/${result.story.id}/write?anon=1`, { replace: true });
    } catch (err: any) {
      setJoinError(err.message || 'Failed to join story');
      setShowNamePrompt(false);
    }
  };

  // Start anonymous join flow
  const handleStartAnonymousJoin = () => {
    setShowNamePrompt(true);
  };

  const handleLoginToJoin = () => {
    if (code) {
      localStorage.setItem('pendingJoinCode', code);
    }
    navigate(withLocale(uiLocale, '/login'));
  };

  // Auto-join after returning from login
  useEffect(() => {
    const pendingCode = localStorage.getItem('pendingJoinCode');
    if (
      isAuthenticated &&
      pendingCode &&
      pendingCode === code &&
      !hasAutoJoined.current &&
      data?.story &&
      data.story.status === 'active'
    ) {
      hasAutoJoined.current = true;
      handleAuthenticatedJoin();
    }
  }, [isAuthenticated, code, data]);

  if (isLoading) {
    return (
      <Layout title="Loading...">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (error || !data?.story) {
    return (
      <Layout title="Story Not Found">
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">🔍</span>
          <h2 className="text-xl font-semibold mb-2">Story not found</h2>
          <p className="text-gray-500 mb-6">
            This story link may be invalid or expired.
          </p>
          <a
            href={withLocale(uiLocale, '/')}
            className="text-primary-600 font-medium hover:underline"
          >
            Go to homepage
          </a>
        </div>
      </Layout>
    );
  }

  const { story } = data;
  const isCompleted = story.status === 'completed';
  const isFull = story.participants.length >= story.maxParticipants;

  if (isCompleted) {
    navigate(`/story/${story.id}/completed`, { replace: true });
    return null;
  }

  return (
    <Layout title="Join Story">
      <div className="space-y-6">
        <AppBanner />

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="text-center mb-6">
            <span className="text-5xl mb-3 block">📖</span>
            <h2 className="text-xl font-bold">You're invited to a story!</h2>
            <p className="text-gray-500 mt-1">
              {GENRE_LABELS[story.genre]} • {story.participants.length}/{story.maxParticipants} writers
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">How it works</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex gap-2">
                <span>✍️</span>
                <span>You'll see only the previous sentence</span>
              </li>
              <li className="flex gap-2">
                <span>📝</span>
                <span>Write your sentence to continue the story</span>
              </li>
              <li className="flex gap-2">
                <span>✨</span>
                <span>AI creates the final story when everyone's done</span>
              </li>
            </ul>
          </div>

          {isFull ? (
            <div className="text-center py-4">
              <p className="text-gray-500">This story is full</p>
              <p className="text-sm text-gray-400 mt-1">
                Download the app to start your own story!
              </p>
            </div>
          ) : showNamePrompt ? (
            <>
              {joinError && (
                <p className="text-red-500 text-center text-sm mb-4">{joinError}</p>
              )}
              <NamePrompt
                onSubmit={handleAnonymousJoin}
                isLoading={joinAnonymousMutation.isPending}
                savedName={savedDisplayName}
              />
            </>
          ) : (
            <div className="space-y-3">
              {joinError && (
                <p className="text-red-500 text-center text-sm">{joinError}</p>
              )}

              {isAuthenticated ? (
                // Signed-in: only "Join as {name}". The anonymous path would
                // re-prompt for a display name and create an unlinked anon
                // participant, which is confusing and breaks turn detection
                // on subsequent visits.
                <button
                  onClick={handleAuthenticatedJoin}
                  disabled={joinMutation.isPending}
                  className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition disabled:opacity-60"
                >
                  {joinMutation.isPending ? 'Joining...' : `Join as ${user?.name}`}
                </button>
              ) : (
                <>
                  <button
                    onClick={handleLoginToJoin}
                    className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition"
                  >
                    Sign in to join
                  </button>
                  <button
                    onClick={handleStartAnonymousJoin}
                    className="w-full bg-white text-gray-600 py-3 rounded-xl font-medium border border-gray-200 hover:bg-gray-50 transition"
                  >
                    Continue without account
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-sm text-gray-400">
          Story code: {code}
        </p>
      </div>
    </Layout>
  );
};

export default JoinPage;
